import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { X } from 'lucide-react'
import type { ChatMode, SchedulePreview, TaskAction, TaskInput, TaskLimits, TaskNotify } from '@shared/types'
import { MODELS, MODEL_IDS, DEFAULT_MODEL } from '@shared/models'
import { useStore } from '../../store'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'
import { Row, ScheduleBuilder, SchedulePreviewBox, Segmented, draftFromSchedule, field, newDraft, scheduleFromDraft, type ScheduleDraft } from './schedule-ui'

const MODE_OPTS: { value: ChatMode | ''; label: string }[] = [
  { value: '', label: 'The chat’s own mode' },
  { value: 'fast', label: 'Fast — quick, low reasoning' },
  { value: 'auto', label: 'Auto — adapts to the task' },
  { value: 'expert', label: 'Expert — deep' },
  { value: 'heavy', label: 'Heavy — multi-agent (reports only)' },
]

/** The task form (a modal over any view): new or edit, opened from the Tasks page or a chat. */
export function TaskEditorHost() {
  const editor = useStore((s) => s.taskEditor)
  const open = useStore((s) => s.openTaskEditor)
  return <AnimatePresence>{editor && <TaskEditor key={editor.task?.id ?? 'new'} onClose={() => open(null)} />}</AnimatePresence>
}

function TaskEditor({ onClose }: { onClose: () => void }) {
  const editor = useStore((s) => s.taskEditor)!
  const chats = useStore((s) => s.chats)
  const saveTask = useStore((s) => s.saveTask)
  const task = editor.task
  const preset = editor.preset ?? {}
  const [title, setTitle] = useState(task?.title ?? preset.title ?? '')
  const [action, setAction] = useState<TaskAction>(task?.action ?? preset.action ?? 'ai')
  const [runIn, setRunIn] = useState<'chat' | 'new'>(task?.runIn ?? preset.runIn ?? (preset.chatId ? 'chat' : 'new'))
  const [chatId, setChatId] = useState<string>(task?.chatId ?? preset.chatId ?? '')
  const [draft, setDraft] = useState<ScheduleDraft>(() => (task ? draftFromSchedule(task.schedule) : { ...newDraft(), ...(preset.schedule ? draftFromSchedule(preset.schedule as never) : {}) }))
  const [instructions, setInstructions] = useState(task?.instructions ?? preset.instructions ?? '')
  const [notify, setNotify] = useState<TaskNotify>(task?.notify ?? preset.notify ?? (runIn === 'chat' ? 'auto' : 'both'))
  const [mode, setMode] = useState<ChatMode | ''>(task?.mode ?? preset.mode ?? '')
  const [model, setModel] = useState<string>(task?.model ?? preset.model ?? DEFAULT_MODEL)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [preview, setPreview] = useState<SchedulePreview | null>(null)
  const [limits, setLimits] = useState<TaskLimits | null>(null)
  useEffect(() => {
    api.taskLimits().then(setLimits).catch(() => {})
  }, [])

  const schedule = useMemo(() => scheduleFromDraft(draft), [draft])
  const visibleChats = chats.filter((c) => !c.taskId)
  // Only an AI run in a chat can decide by itself whether a run is worth a Telegram message.
  const canAuto = action === 'ai' && runIn === 'chat'
  const effNotify = !canAuto && notify === 'auto' ? 'both' : notify

  const submit = async () => {
    setErr(null)
    if (!instructions.trim()) return setErr(action === 'remind' ? 'Write the reminder.' : 'Say what the AI should do each run.')
    if (runIn === 'chat' && !chatId) return setErr('Pick the chat it runs in.')
    if (preview && !preview.ok) return setErr(preview.errors?.join(' · ') ?? 'Check the schedule.')
    setBusy(true)
    const input: TaskInput = {
      title: title.trim() || instructions.trim().split('\n')[0].slice(0, 60),
      instructions: instructions.trim(),
      schedule: schedule as TaskInput['schedule'],
      action,
      runIn,
      chatId: runIn === 'chat' ? chatId : null,
      notify: effNotify,
      mode: action === 'ai' ? (mode || null) : null,
      model: action === 'ai' ? model : null,
    }
    try {
      await saveTask(input, task?.id)
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  const perDay = preview?.ok ? (preview.perDay ?? 0) : 0
  const overBudget = action === 'ai' && limits && !limits.wakes.unlimited && perDay > 0 && perDay > limits.wakes.max * 0.5

  return (
    <motion.div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/55" onClick={onClose} />
      <motion.div
        initial={{ y: 16, scale: 0.98, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 16, scale: 0.98, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        className="glass relative my-auto w-full max-w-2xl overflow-hidden"
        style={{ borderRadius: 'var(--radius-lg)' }}
      >
        <header className="flex items-center justify-between gap-3 border-b border-[var(--line-soft)] px-4 py-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="min-w-0 flex-1 bg-transparent text-[15px] font-semibold text-fg placeholder:text-fg-faint focus:outline-none" placeholder={task ? 'Task title' : 'New task'} />
          <button onClick={onClose} className="rounded-md p-1 text-fg-subtle hover:text-fg">
            <X size={16} />
          </button>
        </header>

        <div className="max-h-[calc(100vh-170px)] space-y-4 overflow-y-auto p-4">
          <Row label="Does">
            <Segmented<TaskAction>
              value={action}
              onChange={setAction}
              options={[
                { value: 'ai', label: 'AI run' },
                { value: 'remind', label: 'Reminder (no AI)' },
              ]}
            />
          </Row>
          <Row label="Runs in">
            <div className="flex flex-wrap items-center gap-2">
              <Segmented<'chat' | 'new'>
                value={runIn}
                onChange={(v) => {
                  setRunIn(v)
                  if (v === 'chat' && notify === 'both' && action === 'ai') setNotify('auto')
                }}
                options={[
                  { value: 'chat', label: 'A chat' },
                  { value: 'new', label: 'A new chat each run' },
                ]}
              />
              {runIn === 'chat' && (
                <select value={chatId} onChange={(e) => setChatId(e.target.value)} className={cn(field, 'min-w-0 max-w-[260px]')}>
                  <option value="">Pick a chat…</option>
                  {visibleChats.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="mt-1 text-[11px] text-fg-faint">
              {runIn === 'chat'
                ? 'The AI wakes up inside that chat — the whole conversation is its context, and its answer lands there.'
                : 'Each run is a standalone report in its own chat (reachable from the run history).'}
            </div>
          </Row>

          <div className="rounded-xl border border-[var(--line-soft)] p-3">
            <ScheduleBuilder d={draft} set={(p) => setDraft((d) => ({ ...d, ...p }))} />
            <div className="mt-3">
              <SchedulePreviewBox schedule={schedule} created={task?.createdAt} runs={task?.runCount} onResult={setPreview} />
            </div>
          </div>

          <Row label={action === 'remind' ? 'Message' : 'Instructions'} align="start">
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={action === 'remind' ? 3 : 6}
              placeholder={
                action === 'remind'
                  ? 'e.g. PAYX earnings call starts in 10 minutes.'
                  : 'What should the AI check and report each run? e.g. PAYX pre-market vs Friday’s close; which range it is in (100.30–101.50 / below 99.50 / above 102.20); any new news. Stay quiet if nothing changed.'
              }
              className={cn(field, 'w-full resize-y py-2 leading-relaxed')}
            />
          </Row>

          {action === 'ai' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Row label="AI mode">
                <select value={mode} onChange={(e) => setMode(e.target.value as ChatMode | '')} className={cn(field, 'w-full')}>
                  {MODE_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Row>
              <Row label="Model">
                <select value={model} onChange={(e) => setModel(e.target.value)} className={cn(field, 'w-full')}>
                  {MODEL_IDS.map((id) => (
                    <option key={id} value={id}>
                      {MODELS[id].label}
                    </option>
                  ))}
                </select>
              </Row>
            </div>
          )}

          <Row label="Notify">
            <Segmented<TaskNotify>
              small
              value={effNotify}
              onChange={setNotify}
              options={[
                ...(canAuto ? [{ value: 'auto' as TaskNotify, label: 'AI decides' }] : []),
                { value: 'both', label: 'App + Telegram' },
                { value: 'telegram', label: 'Telegram' },
                { value: 'app', label: 'App only' },
              ]}
            />
            {canAuto && effNotify === 'auto' && <div className="mt-1 text-[11px] text-fg-faint">Telegram only when a run finds something that needs you; quiet checks stay collapsed in the chat.</div>}
          </Row>

          {err && <div className="text-[12px] text-down">{err}</div>}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-[var(--line-soft)] px-4 py-3">
          <div className="text-[11.5px] text-fg-subtle">
            {limits ? (
              <>
                <div className={cn(overBudget ? 'text-down' : 'text-fg-muted')}>
                  {limits.wakes.unlimited ? 'AI wakeups: unlimited on your Grok subscription' : `AI wakeups today ${limits.wakes.used}/${limits.wakes.max}`}
                  {action === 'ai' && perDay >= 1.5 ? ` · this adds ≈ ${perDay}/day` : ''}
                </div>
                {overBudget && <div className="text-fg-faint">Above half the daily budget — raise it in Settings → Automations, or run it less often.</div>}
              </>
            ) : (
              <span className="text-fg-faint">…</span>
            )}
          </div>
          <button onClick={submit} disabled={busy} className="rounded-lg bg-[var(--color-accent)] px-4 py-2 text-[13px] font-semibold text-[var(--color-base)] disabled:opacity-50">
            {busy ? 'Saving…' : task ? 'Save' : 'Create task'}
          </button>
        </footer>
      </motion.div>
    </motion.div>
  )
}
